import { path } from './client'
import type { ApiClient } from './client'
import type {
  CalendarDate,
  Indicator,
  IndicatorDimension,
  IndicatorDirection,
  IndicatorLevel,
  IndicatorMeasure,
  IndicatorPeriod,
} from './types'

/**
 * What a project is trying to move, and whether it moved.
 *
 * Shaped after the IATI 2.03 `result` element rather than invented, because an
 * organization's donor already speaks that language and the export then becomes
 * a serializer instead of a migration.
 *
 * Output, outcome and impact stay apart. Collapsing them is how "we sent 40,000
 * messages" gets reported as impact, and the console renders them in separate
 * bands for that reason. A delivery count cannot be filed as an outcome.
 *
 * Every call in this module used to name a route under
 * `/v1/org/projects/:id/indicators`, and the server has never served one. The
 * console moved rather than the server, on three counts. The organization is
 * taken from the staff token and from nowhere else, so a project id in the path
 * would be a second identifier every route has to check against the token and
 * one of them will eventually forget to. The flat routes are the ones covered by
 * the server's cross-tenant probe suite, so moving them is where the tenancy
 * risk actually lives. And an indicator id is already unique across the
 * organization, which makes the project segment decoration that can disagree
 * with the id beside it.
 *
 * The other half of the divergence was the shape rather than the path. These
 * endpoints return `{ items, nextCursor }` and this module used to type them as
 * bare arrays. That is the more dangerous of the two failures: a 404 fails the
 * request and the screen renders its failure state, whereas an object where an
 * array was promised reaches the screen as data and `rows.filter` throws during
 * render, which is a blank console instead of a readable message.
 */

/**
 * How many indicators one project's framework may run to before this stops
 * reading.
 *
 * A results framework is small. The server caps its own banded summary at 300
 * and says why: a donor logframe past about forty is already unreportable. Five
 * hundred is well clear of that, and a project that somehow exceeds it is shown
 * the first five hundred rather than hanging on a cursor walk. That is a real
 * limitation and it is written down here rather than left to be discovered.
 */
const INDICATOR_CEILING = 500

/**
 * How many reporting cycles one indicator may run to.
 *
 * Monthly reporting over ten years is 120 rows, so no real indicator reaches
 * this. It matters less than it looks in any case: the server orders cycles
 * newest first, so the newest is on the first page and no ceiling here can hide
 * the figure a screen is actually showing.
 */
const PERIOD_CEILING = 500

export interface IndicatorInput {
  code: string
  title: string
  definition?: string | null
  level: IndicatorLevel
  measure: IndicatorMeasure
  direction: IndicatorDirection
  unit?: string | null
  baselineValue?: number | null
  baselineDate?: CalendarDate | null
  dataSource?: string | null
  meansOfVerification?: string | null
  frequency?: string | null
}

/**
 * One reporting cycle, filed whole.
 *
 * This was three calls: a POST to open a period holding a target, and a PATCH
 * against a period id to file the actual against it. It is one PUT now, keyed on
 * the indicator and the two bounds, and the server upserts on exactly that key.
 *
 * The upsert is the better design and the console moved to it rather than the
 * other way round. Separate create and update endpoints mean the caller has to
 * know whether Q2 has been filed yet, and the caller that guesses wrong either
 * collides with a unique index it cannot see or files Q2 twice, and two rows for
 * one quarter is how a chart doubles a number nobody re-derives afterwards.
 * Sending the same cycle again is the same act as filing it, and leaves one row.
 *
 * A target and an actual are still set by different people at different times,
 * which was the argument for splitting them, and that survives: both are
 * optional, so planning a cycle sends the target and reporting one sends both.
 * What does not survive is the screen having to know which verb to use.
 */
export interface PeriodInput {
  periodStart: CalendarDate
  periodEnd: CalendarDate
  targetValue?: number | null
  actualValue?: number | null
  /**
   * Omitted rather than defaulted. The server reads a cycle with an actual in it
   * as `reported` and one without as `planned`, and it is right to: a result
   * sitting under "planned" is a result no report picks up, and the person who
   * typed it has no way to know. Send this only to promote a cycle to
   * `verified`, which is a claim somebody has to make deliberately.
   */
  status?: IndicatorPeriod['status']
  note?: string | null
  /**
   * Disaggregation as rows: `{ name: 'sex', value: 'female', amount: 412 }`.
   *
   * Rows rather than columns, mirroring IATI's `<dimension name value/>`. A
   * column per dimension is a migration every time one project wants to
   * disaggregate by disability status and another does not.
   *
   * The whole breakdown, never a patch, because the server replaces it. Sending
   * part of one alongside a corrected actual leaves a cycle whose parts add up
   * to a number it no longer claims.
   */
  dimensions?: IndicatorDimension[]
}

export function indicatorsApi(http: ApiClient) {
  return {
    /**
     * Every indicator on a project, in ladder order.
     *
     * The cursor is walked here rather than at the call site because the screen
     * reading this renders all three bands at once, and a half-walked framework
     * is an impact band that looks empty because the page ended. That is the
     * exact misreading the bands exist to prevent.
     */
    async list(projectId: string): Promise<Indicator[]> {
      const collected = await http.collect<Indicator>(
        '/v1/org/indicators',
        { projectId },
        { max: INDICATOR_CEILING },
      )
      return collected.items
    },

    get(indicatorId: string) {
      return http.request<Indicator>(path`/v1/org/indicators/${indicatorId}`)
    },

    /**
     * The project is a body field rather than a path segment, which is what the
     * server takes. It is still checked against the caller's organization before
     * anything is written under it, and a project belonging to somebody else
     * comes back as a 404 rather than a 403.
     */
    create(projectId: string, input: IndicatorInput) {
      return http.request<Indicator>('/v1/org/indicators', {
        method: 'POST',
        body: { projectId, ...input },
      })
    },

    /**
     * `level` is absent from what may be edited, and the server refuses it too.
     * An output with a delivery count already filed against it, relabelled as
     * impact, rewrites what a programme claimed without touching a figure.
     */
    update(indicatorId: string, input: Partial<Omit<IndicatorInput, 'level'>>) {
      return http.request<Indicator>(path`/v1/org/indicators/${indicatorId}`, {
        method: 'PATCH',
        body: input,
      })
    },

    /** Takes every cycle and every breakdown with it, by cascade on the server. */
    remove(indicatorId: string) {
      return http.request<void>(path`/v1/org/indicators/${indicatorId}`, { method: 'DELETE' })
    },

    /** Reporting cycles, newest first, which is the order the server returns. */
    async periods(indicatorId: string): Promise<IndicatorPeriod[]> {
      const collected = await http.collect<IndicatorPeriod>(
        path`/v1/org/indicators/${indicatorId}/periods`,
        {},
        { max: PERIOD_CEILING },
      )
      return collected.items
    },

    /** Files a cycle, or corrects the one already filed for the same bounds. */
    recordPeriod(indicatorId: string, input: PeriodInput) {
      return http.request<IndicatorPeriod>(path`/v1/org/indicators/${indicatorId}/periods`, {
        method: 'PUT',
        body: input,
      })
    },

    removePeriod(indicatorId: string, periodId: string) {
      return http.request<void>(path`/v1/org/indicators/${indicatorId}/periods/${periodId}`, {
        method: 'DELETE',
      })
    },
  }
}

export type IndicatorsApi = ReturnType<typeof indicatorsApi>
